var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc lineModel
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Line, Polyline, Polygon, CompoundPath, SymbolShape, } from '../../element';
import { createSymbol, getSymbolShapeFromCenter } from '../../symbol';
import { createRichText } from '../../rich';
import { LegendType } from '../../interface/legend';
import { SeriesModel } from '../../model';
import { coordType } from '../../coord';
import { eventGraph } from '../../action';
import { getRectLabelPosition } from '../../utils/textLayout';
import { getPercent } from '../../utils/math';
import { registerChart } from '../index';
import { getDefaultZoneOption, renderZone, getClipOffset } from './zone';
import { getLttbPoints } from './sample';
import { defaultFilter } from '../../core/dataset';
function splitByNullPoints(points, connectNulls) {
    if (connectNulls) {
        var notNullPoints = points.filter(function (point) { return !point.isNull; });
        return [notNullPoints];
    }
    var ret = [];
    var prevBlank = true;
    var current;
    for (var _i = 0, points_1 = points; _i < points_1.length; _i++) {
        var point = points_1[_i];
        var isNull = point.isNull;
        if (!isNull) {
            if (prevBlank) {
                current = [];
                current.push(point);
                ret.push(current);
            }
            else {
                current.push(point);
            }
            prevBlank = false;
        }
        else {
            prevBlank = true;
        }
    }
    return ret;
}
/**
 * linechart model
 */
var LineSeries = /** @class */ (function (_super) {
    __extends(LineSeries, _super);
    function LineSeries() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'line';
        _this.legendType = LegendType.SERIES_LEGEND;
        _this.legendIcon = 'rect';
        _this.stackable = true;
        _this.lineGroupRef = createRef();
        _this.markerGroupRef = createRef();
        _this._hoverMarkerPoint = new SymbolShape({ display: false });
        return _this;
    }
    LineSeries.prototype.getDefaultOption = function (option) {
        return __assign(__assign({ name: '', type: option.type, closed: null, encode: {
                x: '',
                y: '',
                color: null,
            }, coord: 'cartesian2d', polar: 0, xAxis: 0, yAxis: 0, smooth: false, smoothMonotone: null, step: false, connectNulls: false, showAllMarker: 'auto', clip: true, lineStyle: {
                stroke: null,
                lineWidth: 2,
                opacity: 1,
                cursor: 'pointer',
            }, areaStyle: {
                fill: null,
                fillOpacity: option.pattern ? 0.5 : 0.1,
            }, label: {
                show: false,
                rotate: null,
                position: 'outside',
                distance: 4,
                formatter: function (point) { return point.value; },
                color: '#6E748E',
                fontSize: 12,
                autoHide: true,
            }, marker: {
                show: false,
                symbol: 'circle',
                opacity: 1,
                size: 6,
                stroke: null,
                fill: '#ffffff',
                lineWidth: 2,
                cursor: 'pointer',
            }, hover: {
                marker: {
                    show: true,
                    symbol: 'circle',
                    size: 8,
                    fill: '#ffffff',
                    stroke: null,
                    lineWidth: 2,
                },
                lineStyle: {
                    lineWidth: 3,
                    stroke: null,
                },
            }, selected: {
                marker: {
                    stroke: null,
                    fill: null,
                    lineWidth: null,
                },
            }, animate: null, showLegend: true, visible: true, selectedMode: false, sampling: 'none', areaFillOrigin: 0 }, getDefaultZoneOption()), _super.prototype.getDefaultOption.call(this, option));
    };
    LineSeries.prototype.getInitialState = function () {
        return {
            needAnimation: this.needAnimation(),
            points: [],
            segments: [],
            isRange: false,
        };
    };
    LineSeries.prototype.beforeRender = function () {
        var _this = this;
        if (this.visible()) {
            var coordModle = this.chart.getComponentModel('coord');
            var coord_1 = coordModle.getCoordinate(this.option);
            var valueAxis_1 = coord_1.getValueAxis();
            var valueField_1 = this._encode.getAxisField(valueAxis_1.type);
            var isRange_1 = lodash.isArray(valueField_1);
            var stackDataKey_1 = this.getStackOnDatakey();
            var _a = valueAxis_1.scale, min = _a.min, max = _a.max;
            var fillOrigin = this.option.areaFillOrigin;
            if (fillOrigin === 'min') {
                fillOrigin = min;
            }
            else if (fillOrigin === 'max') {
                fillOrigin = max;
            }
            var stackData_1 = lodash.isArray(valueField_1)
                ? this._dataset.pickFieldValues(valueField_1)
                : this.getDataset().stack(this.getValueField(), stackDataKey_1, defaultFilter, false, fillOrigin);
            var dataset = this.getDataset();
            var percentStackSumList_1 = [];
            var allStackedDataKeys_1 = this.getAllPercentStackDatakey();
            if (this.option.stacking === 'percent') {
                percentStackSumList_1 = this._dataset.sumFields(allStackedDataKeys_1);
            }
            var categoryAxis_1 = coord_1.getCategoryAxis();
            var categoryField_1 = this._encode.getAxisField(categoryAxis_1.type);
            var points = dataset.mapData(function (record, dataIndex) {
                var _a, _b;
                var encodeData = _this._encode.getEncodeData(record, dataIndex);
                var _c = stackData_1[dataIndex], startValue = _c[0], endValue = _c[1];
                if (_this.option.stacking === 'percent') {
                    startValue = getPercent(startValue, percentStackSumList_1[dataIndex], allStackedDataKeys_1, stackDataKey_1.length);
                    endValue = getPercent(endValue, percentStackSumList_1[dataIndex], allStackedDataKeys_1, stackDataKey_1.length + 1);
                }
                var value = record[valueField_1];
                var xValue = record[categoryField_1];
                var percent = _this.option.stacking === 'percent'
                    ? getPercent(value, percentStackSumList_1[dataIndex], allStackedDataKeys_1)
                        .toFixed(2)
                        .replace('.00', '') + "%"
                    : undefined;
                var startPoition = coord_1.dataToPoint((_a = {},
                    _a[valueAxis_1.shortType] = startValue,
                    _a[categoryAxis_1.shortType] = categoryAxis_1.isCategroy() ? dataIndex : xValue,
                    _a));
                var endPosition = coord_1.dataToPoint((_b = {},
                    _b[valueAxis_1.shortType] = endValue,
                    _b[categoryAxis_1.shortType] = categoryAxis_1.isCategroy() ? dataIndex : xValue,
                    _b));
                var x = endPosition.x, y = endPosition.y, outside = endPosition.outside;
                return {
                    encode: encodeData,
                    plotX: x,
                    plotY: y,
                    stackX: startPoition.x,
                    stackY: startPoition.y,
                    name: record[categoryField_1],
                    value: encodeData[valueAxis_1.shortType],
                    color: encodeData.color,
                    visible: !outside,
                    selected: _this.selectedItems.has(record),
                    percent: percent,
                    isNull: !isRange_1 && isNaN(parseFloat(record[valueField_1])),
                    shapeRef: createRef(),
                    labelRef: createRef(),
                };
            });
            this.state.points = points;
            this.state.isRange = isRange_1;
            this.state.segments = splitByNullPoints(points, this.option.connectNulls);
            this.state.segments.forEach(function (segmentPoints) {
                if (segmentPoints.length === 1) {
                    segmentPoints[0].isolated = true;
                }
            });
            var clipPath = coordModle.getCoordClipPath(this.option, getClipOffset(this));
            if (!this.clipPath) {
                this.clipPath = clipPath;
            }
            else if (this.clipPath.type === clipPath.type) {
                this.clipPath.stopAllAnimation().setAttr(clipPath.attr);
            }
            else {
                this.clipPath = clipPath;
            }
        }
        else {
            this.setState({ needAnimation: true });
        }
        this._hoverMarkerPoint.setAttr({ key: this.getKey('hoverPoint') });
    };
    LineSeries.prototype.afterRender = function () {
        if (this.state.needAnimation && this.visible() && this.state.points.length > 0) {
            var coord = this.getCoord();
            var clipOffset = getClipOffset(this);
            if (coord.coordinateSystem === coordType.CARTESIAN2D) {
                var categoryAxis = coord.getCategoryAxis();
                var _a = categoryAxis.parentModel.getState(), top_1 = _a.top, left = _a.left, width = _a.width, height = _a.height;
                top_1 -= clipOffset;
                left -= clipOffset;
                width += clipOffset * 2;
                height += clipOffset * 2;
                if (categoryAxis.type === 'xAxis') {
                    this.clipPath.setAttr({
                        x: left,
                        y: top_1,
                        // fix lynx clip not working when width is zero
                        width: 1e-6,
                        height: height,
                    });
                    this.clipPath.stopAllAnimation().animateTo({
                        x: left,
                        y: top_1,
                        width: width,
                        height: height,
                    }, 1000, 'CubicOut');
                }
                else if (categoryAxis.type === 'yAxis') {
                    this.clipPath.setAttr({
                        x: left,
                        y: top_1,
                        width: width,
                        height: 1e-6,
                    });
                    this.clipPath.stopAllAnimation().animateTo({
                        x: left,
                        y: top_1,
                        width: width,
                        height: height,
                    }, 1000, 'CubicOut');
                }
            }
            else if (coord.coordinateSystem === coordType.POLAR) {
                var _b = coord.getCenter(), cx = _b[0], cy = _b[1];
                this.getRenderObj()
                    .stopAllAnimation()
                    .setAttr({
                    scale: [0, 0],
                    origin: [cx, cy],
                })
                    .animateTo({
                    scale: [1, 1],
                    origin: [cx, cy],
                });
            }
            this.setState({ needAnimation: false });
        }
        else if (this.visible() === false) {
            this.setState({ needAnimation: true });
        }
    };
    LineSeries.prototype.toggleHighlightSeries = function (active) {
        var _a;
        (_a = this.lineGroupRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(__assign({}, this.getLineStyle(active ? this.option.hover.lineStyle : this.option.lineStyle)));
    };
    LineSeries.prototype.toggleBlurSeries = function (blur) {
        _super.prototype.toggleBlurSeries.call(this, blur);
        this.renderObj.setAttr({
            opacity: blur ? 0.4 : 1,
        });
    };
    LineSeries.prototype.toggleHighlightPoint = function (dataIndex, active) {
        var _this = this;
        this.state.hoverPointIndex = active ? dataIndex : null;
        var point = this.state.points[dataIndex];
        if (point.isNull) {
            return;
        }
        var color = point.color, plotX = point.plotX, plotY = point.plotY, selected = point.selected, visible = point.visible;
        var hoverOption = this.option.hover;
        if (active) {
            this._hoverMarkerPoint.setAttr({ display: true });
            var coord = this.getCoord();
            var categoryAxis_2 = coord.getCategoryAxis();
            var size = this.option.hover.marker.size;
            this._hoverMarkerPoint.setAttr(__assign(__assign(__assign({ display: hoverOption.marker.show && (!this.option.clip || visible), symbol: this.option.hover.marker.symbol, x: plotX - size / 2, y: plotY - size / 2, width: size, height: size, stroke: color, fill: color, opacity: point.encode.opacity }, this.getShapeStyle(hoverOption.marker)), (selected ? this.getShapeStyle(this.option.selected.marker) : null)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: point.encode[categoryAxis_2.shortType],
                        point: _this.getWrappedPoint(dataIndex),
                        hasAxis: true,
                    });
                } }));
        }
        else {
            this._hoverMarkerPoint.setAttr({ display: false });
        }
    };
    LineSeries.prototype.toggleSelectPoint = function (dataIndex, selected) {
        var _a;
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        (_a = point.shapeRef) === null || _a === void 0 ? void 0 : _a.current.setAttr(__assign({ fill: point.color, stroke: point.color }, this.getShapeStyle(selected ? this.option.selected.marker : this.option.marker)));
    };
    LineSeries.prototype.renderFillArea = function (areaStyle, color) {
        var _this = this;
        var _a = this.option, coord = _a.coord, closed = _a.closed, clip = _a.clip;
        var stackOnChart = lodash.last(this.getStackOnSeries());
        var pointsGroup = this.state.segments;
        var isCartesian2d = coord === coordType.CARTESIAN2D;
        var notClosed = (isCartesian2d && !closed) || pointsGroup.length !== 1;
        var group = new Group(__assign({ key: this.getKey('area'), clip: clip && this.clipPath, fill: color }, this.getShapeStyle(areaStyle)));
        pointsGroup.forEach(function (points, index) {
            var prevStep = stackOnChart ? stackOnChart.getOption().step : false;
            var prevSmooth = stackOnChart && !prevStep
                ? stackOnChart.getOption().smooth
                : _this.option.smooth && !_this.option.step;
            var prevSmoothMonotone = stackOnChart && !prevStep ? stackOnChart.getSmoothMontone() : null;
            var _a = _this._handleStepPoints(points, prevStep), plotPoints = _a.plotPoints, stackPoints = _a.stackPoints;
            plotPoints = _this._handleSamplePoints(plotPoints);
            stackPoints = _this._handleSamplePoints(stackPoints);
            if (isCartesian2d) {
                var gridState_1 = _this.chart.getComponentModel('coord').grid.getState();
                stackPoints.forEach(function (point) {
                    point.y = lodash.clamp(point.y, gridState_1.top, gridState_1.bottom);
                });
            }
            stackPoints.reverse();
            var onAreaEvent = function (event) {
                return _this.handleUIEvent(event, eventGraph.AREA, {
                    seriesIndex: _this.seriesIndex,
                    seriesName: _this.option.name,
                });
            };
            if (notClosed) {
                var chartLine = new Polyline({
                    pointList: plotPoints,
                    smooth: _this.option.smooth && !_this.option.step,
                    smoothMonotone: _this.getSmoothMontone(),
                });
                var stackLine = new Polyline({
                    pointList: stackPoints,
                    smooth: prevSmooth,
                    smoothMonotone: prevSmoothMonotone,
                });
                var connectLine = new Line({
                    x1: lodash.last(plotPoints).x,
                    y1: lodash.last(plotPoints).y,
                    x2: stackPoints[0].x,
                    y2: stackPoints[0].y,
                });
                var endLine = new Line({
                    x1: lodash.last(stackPoints).x,
                    y1: lodash.last(stackPoints).y,
                    x2: lodash.first(plotPoints).x,
                    y2: lodash.first(plotPoints).y,
                });
                var fillArea = new CompoundPath({
                    shapes: [chartLine, connectLine, stackLine, endLine],
                });
                fillArea.setAttr({
                    key: _this.getKey("area" + index.toString()),
                    display: _this.visible(),
                    // smooth: this.option.smooth && !this.option.step,
                    transitionProperty: _this.getTransitionProperty(),
                    onEvent: onAreaEvent,
                });
                group.add(fillArea);
            }
            else {
                var fillArea = new Polygon({
                    pointList: plotPoints,
                    smooth: _this.option.smooth && !_this.option.step,
                });
                fillArea.setAttr({
                    key: _this.getKey("area" + index.toString()),
                    display: _this.visible(),
                    transitionProperty: _this.getTransitionProperty(),
                    onEvent: onAreaEvent,
                });
                group.add(fillArea);
            }
        });
        return group;
    };
    LineSeries.prototype.renderLine = function (lineStyle, color, ref, zone) {
        var _this = this;
        var _a = this.option, coord = _a.coord, closed = _a.closed, clip = _a.clip;
        var pointsGroup = this.state.segments;
        var notClosed = (coord === coordType.CARTESIAN2D && !closed) || pointsGroup.length !== 1;
        // not closed in 2d, closed in other coords
        var ChartLineComponent = notClosed ? Polyline : Polygon;
        var group = new Group(__assign({ key: 'line-line', ref: ref, clip: clip && this.clipPath, display: this.visible(), opacity: this.visible() ? 1 : 0, stroke: color }, this.getLineStyle(lineStyle)));
        pointsGroup.forEach(function (points, index) {
            var plotPoints = _this._handleStepPoints(points).plotPoints;
            group.add(new ChartLineComponent({
                key: _this.getKey("line-line" + index.toString()),
                pointList: _this._handleSamplePoints(plotPoints),
                smooth: _this.option.smooth && !_this.option.step,
                smoothMonotone: _this.getSmoothMontone(),
                transitionProperty: _this.getTransitionProperty(),
                onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LINE, {
                        seriesIndex: _this.seriesIndex,
                        seriesName: _this.option.name,
                        zone: zone,
                    });
                },
            }));
        });
        return group;
    };
    LineSeries.prototype.getSmoothMontone = function () {
        var shortType = this.getCoord().getCategoryAxis().shortType;
        return shortType === 'x' ? 'x' : undefined;
    };
    LineSeries.prototype.renderMarkers = function () {
        var _this = this;
        var _a = this.option, showAllMarker = _a.showAllMarker, encode = _a.encode;
        var _b = this.option.marker, show = _b.show, size = _b.size, lineWidth = _b.lineWidth, fill = _b.fill, stroke = _b.stroke;
        var colorEncode = encode.color;
        var seriesColor = this.getSeriesColor();
        var transitionProperty = this.getTransitionProperty();
        var coord = this.getCoord();
        var groupClip = coord.coordinateSystem === 'cartesian2d'
            ? this.chart.getComponentModel('coord').grid.getClipPath(size / 2 + lineWidth + 1)
            : null;
        var group = new Group(__assign(__assign({ key: this.getKey('line-marker'), ref: this.markerGroupRef, fill: seriesColor, stroke: seriesColor }, this.getShapeStyle(this.option.marker)), { clip: groupClip }));
        var categoryAxis = coord.getCategoryAxis();
        var intervalWidth = Math.abs(1 / categoryAxis.getPixelValue());
        var visibleTicks = categoryAxis.getIntervalTicks();
        this.state.points.forEach(function (point, dataIndex) {
            var _a = point.encode.opacity, opacity = _a === void 0 ? 1 : _a;
            if (point.isNull) {
                return;
            }
            if (!show && !point.isolated) {
                return;
            }
            var onEvent = function (event) {
                return _this.handleUIEvent(event, 'point', {
                    title: point.encode[categoryAxis.shortType],
                    point: _this.getWrappedPoint(dataIndex),
                    hasAxis: true,
                });
            };
            var pointSize = _this.visible() && point.visible ? size : 0;
            var shapeParam = getSymbolShapeFromCenter(point.plotX, point.plotY, pointSize);
            var isVisibleTicks = visibleTicks.indexOf(dataIndex) !== -1;
            // default showAllMarker is 'auto'
            var showMarker = !(intervalWidth < size * 1.5 && !isVisibleTicks);
            if (showAllMarker === true || point.isolated) {
                showMarker = true;
            }
            else if (showAllMarker === false) {
                showMarker = isVisibleTicks;
            }
            group.add(createSymbol(_this.option.marker, shapeParam, __assign(__assign(__assign({ display: showMarker, ref: point.shapeRef, opacity: opacity,
                transitionProperty: transitionProperty }, (colorEncode
                ? _this.getShapeStyle({
                    fill: fill || point.color,
                    stroke: stroke || point.color,
                })
                : null)), { onEvent: onEvent }), (point.selected ? _this.getShapeStyle(_this.option.selected.marker) : null))));
        });
        return group;
    };
    LineSeries.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('line-label'),
        });
        var _a = this.option.label, position = _a.position, distance = _a.distance, autoHide = _a.autoHide;
        var size = this.option.marker.size;
        var labels = this.state.points.map(function (point, dataIndex) {
            var plotX = point.plotX, plotY = point.plotY, color = point.color;
            var textPosition = getRectLabelPosition({
                top: plotY - size / 2,
                left: plotX - size / 2,
                right: plotX + size / 2,
                bottom: plotY + size / 2,
            }, position, distance);
            if (point.isNull || !point.visible || !_this.visible()) {
                return;
            }
            return createRichText(__assign(__assign(__assign({ key: dataIndex, ref: point.labelRef, text: _this.getDataLabel(dataIndex), fill: color }, textPosition), _this.getTextStyle(_this.option.label)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'label', {
                        point: _this.getWrappedPoint(dataIndex),
                    });
                } }));
        });
        autoHide && this.chart.__autoHideLabel(labels);
        return group.addAll(labels);
    };
    LineSeries.prototype.render = function () {
        var _this = this;
        var coord = this.getCoord();
        var group = new Group({
            key: this.getKey('line'),
            origin: coord.coordinateSystem === coordType.POLAR ? coord.getCenter() : [0, 0],
            onEvent: function (event) { return _this.handleSeriesUIEvent(event); },
        });
        if (!this.visible() && this.chart.getOption().transition === false) {
            return null;
        }
        if (this.option.type === 'area') {
            group.add(renderZone(this, 'area'));
        }
        if (!(this.state.isRange && this.type === 'area')) {
            group.add(renderZone(this, 'line'));
        }
        this.chart.addToTopLevel(this.renderMarkers());
        if (this.option.label.show) {
            this.chart.addToTopLevel(this.renderLabels(), 1);
        }
        this.chart.addToTopLevel(this._hoverMarkerPoint);
        return group;
    };
    LineSeries.prototype._handleStepPoints = function (points, prevStackStep) {
        if (prevStackStep === void 0) { prevStackStep = false; }
        var isReverse = this.getCoord().getCategoryAxis().type === 'yAxis';
        var step = this.option.step;
        if (step || prevStackStep) {
            var stepType_1 = step && (typeof step === 'boolean' ? (isReverse ? 'vh' : 'hv') : step);
            var prevStackStepType_1 = prevStackStep &&
                (typeof prevStackStep === 'boolean' ? (isReverse ? 'vh' : 'hv') : prevStackStep);
            var plotPoints_1 = [];
            var stackPoints_1 = [];
            points.forEach(function (point, pointIndex) {
                if (pointIndex === 0) {
                    plotPoints_1.push({ x: point.plotX, y: point.plotY });
                    stackPoints_1.push({ x: point.stackX, y: point.stackY });
                }
                else {
                    if (stepType_1 === 'hv') {
                        plotPoints_1.push({ x: point.plotX, y: points[pointIndex - 1].plotY });
                    }
                    else if (stepType_1 === 'vh') {
                        plotPoints_1.push({ x: points[pointIndex - 1].plotX, y: point.plotY });
                    }
                    else if (stepType_1 === 'vhv') {
                        var plotStepH = (point.plotY + points[pointIndex - 1].plotY) / 2;
                        plotPoints_1.push({ x: points[pointIndex - 1].plotX, y: plotStepH });
                        plotPoints_1.push({ x: point.plotX, y: plotStepH });
                    }
                    else if (stepType_1 === 'hvh') {
                        var plotStepV = (point.plotX + points[pointIndex - 1].plotX) / 2;
                        plotPoints_1.push({ x: plotStepV, y: points[pointIndex - 1].plotY });
                        plotPoints_1.push({ x: plotStepV, y: point.plotY });
                    }
                    if (prevStackStepType_1 === 'hv') {
                        stackPoints_1.push({ x: point.stackX, y: points[pointIndex - 1].stackY });
                    }
                    else if (prevStackStepType_1 === 'vh') {
                        stackPoints_1.push({ x: point.stackX, y: points[pointIndex - 1].stackY });
                    }
                    else if (prevStackStepType_1 === 'vhv') {
                        var stackStepH = (point.stackY + points[pointIndex - 1].stackY) / 2;
                        stackPoints_1.push({ x: points[pointIndex - 1].stackX, y: stackStepH });
                        stackPoints_1.push({ x: point.stackX, y: stackStepH });
                    }
                    else if (prevStackStepType_1 === 'hvh') {
                        var stackStepV = (point.stackX + points[pointIndex - 1].stackX) / 2;
                        stackPoints_1.push({ x: stackStepV, y: points[pointIndex - 1].stackY });
                        stackPoints_1.push({ x: stackStepV, y: point.stackY });
                    }
                    plotPoints_1.push({ x: point.plotX, y: point.plotY });
                    stackPoints_1.push({ x: point.stackX, y: point.stackY });
                }
            });
            return {
                plotPoints: stepType_1
                    ? plotPoints_1
                    : points.map(function (point) { return ({ x: point.plotX, y: point.plotY }); }),
                stackPoints: prevStackStepType_1
                    ? stackPoints_1
                    : points.map(function (point) { return ({ x: point.stackX, y: point.stackY }); }),
            };
        }
        return {
            plotPoints: points.map(function (point) { return ({ x: point.plotX, y: point.plotY }); }),
            stackPoints: points.map(function (point) { return ({ x: point.stackX, y: point.stackY }); }),
        };
    };
    LineSeries.prototype._handleSamplePoints = function (points) {
        if (!this.option.step && this.option.sampling === 'lttb') {
            var _a = this.getCoord().getCategoryAxis().scale, start = _a.start, end = _a.end;
            var threshold = Math.floor(Math.abs(end - start) * (this.chart.getRenderInstance().dpr || 1));
            if (threshold < points.length) {
                points = getLttbPoints(points, threshold);
            }
        }
        return points;
    };
    LineSeries.dependencies = ['coord', 'legend'];
    return LineSeries;
}(SeriesModel));
export default LineSeries;
registerChart('line', LineSeries);
registerChart('area', LineSeries);
